import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle, CalendarClock, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const DURATIONS = [
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Admin-only "Edit Series" dialog.
 *
 * Edits THIS occurrence and all future occurrences (same `recurrence_id`,
 * `date >= pivot.date`). No price/refund flow — backend re-derives price
 * from the new lane+duration but performs zero Stripe movement.
 */
export default function EditSeriesDialog({
  open,
  onOpenChange,
  pivotBooking,      // the booking the admin clicked "Edit Series" on
  lanes = [],        // [{id, name}]
  onSaved,           // () => void   (refreshes parent after success)
}) {
  const [laneId, setLaneId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!pivotBooking) return;
    setLaneId(pivotBooking.lane_id || '');
    setStartTime(pivotBooking.start_time || '');
    setDuration(String(pivotBooking.duration || 30));
    setEndDate('');
    setReason('');
    setConflicts([]);
  }, [pivotBooking]);

  const isValid = useMemo(
    () => Boolean(laneId && /^\d{2}:\d{2}$/.test(startTime) && duration),
    [laneId, startTime, duration],
  );

  const hasChanges = useMemo(() => {
    if (!pivotBooking) return false;
    return (
      laneId !== pivotBooking.lane_id ||
      startTime !== pivotBooking.start_time ||
      String(pivotBooking.duration) !== String(duration) ||
      Boolean(endDate)
    );
  }, [pivotBooking, laneId, startTime, duration, endDate]);

  const handleSave = async () => {
    if (!pivotBooking || !isValid) return;
    setSaving(true);
    setConflicts([]);
    try {
      const payload = {
        lane_id: laneId,
        start_time: startTime,
        duration,
      };
      if (endDate) payload.end_date = endDate;
      if (reason.trim()) payload.reason = reason.trim().slice(0, 300);

      const { data } = await api.put(
        `/lanes/admin/bookings/${pivotBooking.id}/edit-series`,
        payload,
      );
      const updated = data?.updated_count ?? 0;
      const cancelled = data?.cancelled_count ?? 0;
      toast.success(
        cancelled > 0
          ? `Updated ${updated} booking${updated === 1 ? '' : 's'} · cancelled ${cancelled} beyond end date`
          : `Updated ${updated} booking${updated === 1 ? '' : 's'} in the series`,
      );
      onOpenChange?.(false);
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Conflict payload is a structured object
      if (e?.response?.status === 409 && detail?.conflicts) {
        setConflicts(detail.conflicts);
        toast.error(detail.message || 'Conflicts detected');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to edit series');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-series-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-blue-600" />
            Edit recurring series
          </DialogTitle>
          <DialogDescription>
            Applies to this booking and all <strong>future</strong> occurrences
            in the same series. Past and cancelled bookings are not touched.
          </DialogDescription>
        </DialogHeader>

        {pivotBooking && (
          <div className="text-xs bg-slate-50 border rounded-md p-3 space-y-0.5">
            <div>
              <span className="text-slate-500">Series starts from:</span>{' '}
              <strong>{formatDate(pivotBooking.date)}</strong>
            </div>
            <div>
              <span className="text-slate-500">Current slot:</span>{' '}
              {pivotBooking.lane_name || `Lane ${pivotBooking.lane_id}`} ·{' '}
              {pivotBooking.start_time} · {pivotBooking.duration} min
            </div>
          </div>
        )}

        <div className="space-y-3 pt-2">
          <div>
            <Label htmlFor="series-lane">Lane</Label>
            <Select value={laneId} onValueChange={setLaneId}>
              <SelectTrigger id="series-lane" data-testid="series-lane-select">
                <SelectValue placeholder="Select lane" />
              </SelectTrigger>
              <SelectContent>
                {lanes.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name || `Lane ${l.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="series-start">Start time</Label>
              <Input
                id="series-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="series-start-time-input"
              />
            </div>
            <div>
              <Label htmlFor="series-duration">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger id="series-duration" data-testid="series-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="series-end">
              New series end date <span className="text-slate-400">(optional — shortens the series)</span>
            </Label>
            <Input
              id="series-end"
              type="date"
              value={endDate}
              min={pivotBooking?.date || ''}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="series-end-date-input"
            />
          </div>

          <div>
            <Label htmlFor="series-reason">
              Reason <span className="text-slate-400">(optional)</span>
            </Label>
            <Input
              id="series-reason"
              placeholder="e.g. Lane 2 maintenance swap"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              data-testid="series-reason-input"
            />
          </div>
        </div>

        {conflicts.length > 0 && (
          <Alert variant="destructive" className="mt-2" data-testid="series-conflicts-alert">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-1">
                {conflicts.length} occurrence{conflicts.length === 1 ? '' : 's'} conflict with the new slot:
              </div>
              <ul className="list-disc pl-5 space-y-0.5 text-xs max-h-32 overflow-y-auto">
                {conflicts.slice(0, 8).map((c) => (
                  <li key={c.date}>
                    <span className="font-mono">{c.date}</span> — {c.reason}
                  </li>
                ))}
                {conflicts.length > 8 && (
                  <li className="italic text-slate-500">
                    + {conflicts.length - 8} more
                  </li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || !hasChanges || saving}
            data-testid="edit-series-save-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {saving ? 'Updating…' : 'Apply to all future'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
